import { useEffect, useRef, useState } from 'react';

/**
 * How much is waiting to be sent, kept in the top bar.
 *
 * It replaces a bar that sat pinned over the bottom of the review queue and
 * covered the card being worked on. Deliberately not a shopping cart: nothing
 * is being bought, and the icon invited the wrong idea about what the button
 * does. It is a stack of cards, which is literally what the batch holds.
 *
 * The count animates on the way up, so a decision landing is what catches the
 * eye rather than the button sitting there. The global reduced-motion rule
 * turns that off for anyone who asked it to.
 */
export default function BatchButton({
  count, onClick,
}: { count: number; onClick: () => void }) {
  const [bump, setBump] = useState(false);
  const previous = useRef(count);

  useEffect(() => {
    if (count === previous.current) return;
    const grew = count > previous.current;
    previous.current = count;
    if (!grew) return;
    setBump(true);
    const timer = setTimeout(() => setBump(false), 480);
    return () => clearTimeout(timer);
  }, [count]);

  return (
    <button
      className={`batchbtn${bump ? ' bump' : ''}`}
      data-filled={count > 0}
      onClick={onClick}
      aria-label={`My batch, ${count} item${count === 1 ? '' : 's'}`}
      title={count
        ? `${count} decision${count === 1 ? '' : 's'} waiting to be sent`
        : 'Nothing in your batch yet'}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="8" width="13" height="12" rx="2" />
        <path d="M7 5.2A1.2 1.2 0 0 1 8.2 4H19a2 2 0 0 1 2 2v9.8a1.2 1.2 0 0 1-1.2 1.2" />
      </svg>
      <span className="lbl">My batch</span>
      <span className="n">{count}</span>
    </button>
  );
}
